import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { Reconciliation } from '@/reducers/admin/financialAccountsReducer'
import { BookkeepingReport } from '@/reducers/finances/bookkeepingReportsReducer'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'

const sortByEndDateDesc = (reconciliations: Reconciliation[]) =>
  orderBy(reconciliations, 'endingBalanceDate', 'desc')

export const findPreviousReconciliation = (
  bookkeepingReport: BookkeepingReport | undefined,
  accountReconciliations: Reconciliation[]
) => {
  if (!bookkeepingReport) return null

  const previousRecons = sortByEndDateDesc(accountReconciliations).filter(
    (recon) => recon.endingBalanceDate < bookkeepingReport.startDate
  )

  return previousRecons.length > 0 ? previousRecons[0] : null
}

/** The bookkeeping month a reconciliation belongs to: the month its period closes in. */
const reportMonthForClose = (endingBalanceDate: string) =>
  DateTime.fromISO(endingBalanceDate, { zone: 'utc' }).toFormat(
    DATE_FORMATS_LUXON.YEAR_MONTH
  )

type ReconciliationPeriod = {
  startingBalanceDate: string
  endingBalanceDate: string
}

type ReportPeriod = { date: string; startDate: string; endDate: string }

const closesInReportMonth = (
  reconciliation: ReconciliationPeriod,
  report: ReportPeriod
) => reportMonthForClose(reconciliation.endingBalanceDate) === report.date

/**
 * CUBK auto reconciliations legitimately span many months while being attached to a
 * single report, so the months in between are linked by nothing but this containment.
 */
const spansReportPeriod = (
  reconciliation: ReconciliationPeriod,
  report: ReportPeriod
) =>
  report.startDate >= reconciliation.startingBalanceDate &&
  report.endDate <= reconciliation.endingBalanceDate

export const reportsCoveredByReconciliation = <T extends ReportPeriod>(
  reports: T[],
  reconciliation: ReconciliationPeriod
): T[] =>
  reports.filter(
    (report) =>
      closesInReportMonth(reconciliation, report) ||
      spansReportPeriod(reconciliation, report)
  )
